import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import axios from "axios";
import { toast } from "react-toastify";
import {
  CRow,
  CCol,
  CCard,
  CCardHeader,
  CCardBody,
  CButton,
  CBadge,
  CFormCheck,
  CFormSelect,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CListGroup,
  CListGroupItem,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilChevronBottom, cilChevronTop, cilInfo, cilMap, cilMediaPlay, cilWarning } from "@coreui/icons";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveLine } from "@nivo/line";
import dayjs from "dayjs";
import SafeTooltip from "../../../components/SafeTooltip/SafeTooltip";
import TruckMap from "./TruckMap";
import DowntimeModal from "./DowntimeModal";
import { getFakeLocalDate } from "../utils/timeUtils";
import {
  ACT,
  PHASE_META,
  IDLE_STATE_LABELS,
  durationOf,
  fmtNum,
  tripStatusChips,
  speedColor,
  percentile,
} from "../utils/truckActivity";

// Дашборд аналитики самосвала.
// Кладется в src/core/pages/CameraDetailPage/components/TruckAnalyticsDashboard.jsx

// Зум колесом и перетаскивание для графиков Nivo (как в AnalyticsDashboard экскаватора)
const ZoomableNivoWrapper = ({ dataLength, minWindow = 5, children }) => {
  const [bounds, setBounds] = useState({ start: 0, end: Math.max(0, dataLength - 1) });
  const containerRef = useRef(null);
  const isPanning = useRef(false);
  const lastX = useRef(0);

  useEffect(() => {
    setBounds({ start: 0, end: Math.max(0, dataLength - 1) });
  }, [dataLength]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || dataLength <= 1) return undefined;

    const handleWheel = (e) => {
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      const pct = (e.clientX - rect.left) / rect.width;

      setBounds((prev) => {
        const curLen = prev.end - prev.start;
        const factor = e.deltaY > 0 ? 1.25 : 0.8;
        let newLen = curLen * factor;

        const actualMin = Math.min(minWindow, dataLength - 1);
        if (newLen < actualMin) newLen = actualMin;
        if (newLen >= dataLength - 1) newLen = dataLength - 1;

        const anchor = prev.start + curLen * pct;
        let newStart = anchor - newLen * pct;
        let newEnd = newStart + newLen;

        if (newStart < 0) {
          newStart = 0;
          newEnd = Math.min(newStart + newLen, dataLength - 1);
        }
        if (newEnd > dataLength - 1) {
          newEnd = dataLength - 1;
          newStart = Math.max(0, newEnd - newLen);
        }
        return { start: newStart, end: newEnd };
      });
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => container.removeEventListener("wheel", handleWheel);
  }, [dataLength, minWindow]);

  const onPointerDown = (e) => {
    if (dataLength <= 1) return;
    isPanning.current = true;
    lastX.current = e.clientX;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!isPanning.current || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const deltaX = e.clientX - lastX.current;
    lastX.current = e.clientX;

    setBounds((prev) => {
      const curLen = prev.end - prev.start;
      const shift = -(deltaX / rect.width) * curLen;
      let newStart = prev.start + shift;
      let newEnd = prev.end + shift;

      if (newStart < 0) {
        newStart = 0;
        newEnd = curLen;
      }
      if (newEnd > dataLength - 1) {
        newEnd = dataLength - 1;
        newStart = Math.max(0, newEnd - curLen);
      }
      return { start: newStart, end: newEnd };
    });
  };

  const onPointerUp = (e) => {
    isPanning.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <div
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        width: "100%",
        height: "100%",
        cursor: isPanning.current ? "grabbing" : "grab",
        touchAction: "none",
        overflow: "hidden",
      }}
    >
      {children(Math.max(0, Math.floor(bounds.start)), Math.min(dataLength, Math.ceil(bounds.end) + 1))}
    </div>
  );
};

const KpiCard = ({ title, value, unit, valueClass = "text-dark", sub, tooltip }) => {
  const head = (
    <div className="text-muted small text-uppercase fw-bold mb-2" style={tooltip ? { cursor: "help" } : undefined}>
      {title} {tooltip && <CIcon icon={cilInfo} size="sm" className="text-info" />}
    </div>
  );

  return (
    <CCol xs={6} md={4} xl={3}>
      <CCard className="h-100 shadow-sm border-0 bg-light">
        <CCardBody className="text-center d-flex flex-column p-3">
          {tooltip ? <SafeTooltip content={tooltip}>{head}</SafeTooltip> : head}
          <div className="flex-grow-1 d-flex align-items-center justify-content-center">
            <div className={`fs-3 fw-bold lh-1 ${valueClass}`}>
              {value} {unit && <span className="fs-6">{unit}</span>}
            </div>
          </div>
          <div className="mt-2 d-flex align-items-center justify-content-center" style={{ minHeight: "20px" }}>
            {sub}
          </div>
        </CCardBody>
      </CCard>
    </CCol>
  );
};

// Подсказка у курсора: рисуется в body (position: fixed), поэтому таблица с прокруткой ее не обрезает.
// Ширина - по содержимому (длинное название фазы целиком в одну строку). У правого края экрана
// подсказка встает слева от курсора, чтобы не уйти за край.
const TOOLTIP_FLIP_PX = 360;

const CursorTooltip = ({ tip }) => {
  if (!tip) return null;
  const flip = tip.x > window.innerWidth - TOOLTIP_FLIP_PX;
  return createPortal(
    <div
      className="bg-dark text-white rounded shadow px-2 py-1 small"
      style={{
        position: "fixed",
        left: flip ? tip.x - 12 : tip.x + 12,
        top: tip.y + 14,
        transform: flip ? "translateX(-100%)" : undefined,
        zIndex: 2000,
        pointerEvents: "none",
        width: "max-content",
        maxWidth: "min(90vw, 520px)",
        whiteSpace: "nowrap",
      }}
    >
      {tip.content}
    </div>,
    document.body
  );
};

// Полоса фаз рейса. При наведении на часть полосы - фаза, ее длительность и доля рейса
const PhaseBar = ({ phases, total, height = 12, formatDuration }) => {
  const [tip, setTip] = useState(null);
  const sum = total || PHASE_META.reduce((acc, p) => acc + (Number(phases?.[p.key]) || 0), 0);
  if (!sum) return <div className="text-muted small">—</div>;

  const fmt = (v) => (formatDuration ? formatDuration(v) : `${Math.round(v)} с`);

  return (
    <>
      <div
        className="d-flex w-100 rounded overflow-hidden shadow-sm"
        style={{ height: `${height}px`, backgroundColor: "#e9ecef", cursor: "help" }}
        onMouseLeave={() => setTip(null)}
      >
        {PHASE_META.map((p) => {
          const value = Number(phases?.[p.key]) || 0;
          if (value <= 0) return null;
          const content = (
            <span className="d-inline-flex align-items-center gap-2">
              <span
                className="d-inline-block rounded-circle"
                style={{ width: "10px", height: "10px", backgroundColor: p.color, border: "1px solid #fff" }}
              />
              <span>
                <b>{p.label}</b>: {fmt(value)} · {Math.round((value / sum) * 100)}%
              </span>
            </span>
          );
          return (
            <div
              key={p.key}
              style={{ width: `${(value / sum) * 100}%`, backgroundColor: p.color }}
              onMouseMove={(e) => setTip({ x: e.clientX, y: e.clientY, content })}
            />
          );
        })}
      </div>
      <CursorTooltip tip={tip} />
    </>
  );
};

// График скорости по ходу рейса. Цвет линии на каждом участке - как на карте (красный - медленно)
const SPARK_W = 110;
const SPARK_H = 26;
const SPARK_POINTS = 80;

const SpeedSparkline = ({ series, lo, hi }) => {
  if (!series || series.length < 2) return <span className="text-muted small">нет GPS</span>;
  const step = Math.max(1, Math.ceil(series.length / SPARK_POINTS));
  const pts = series.filter((_, i) => i % step === 0);
  const t0 = pts[0].t;
  const tSpan = Math.max(1, pts[pts.length - 1].t - t0);
  const vMax = Math.max(1, ...pts.map((p) => p.v));
  const xy = pts.map((p) => [((p.t - t0) / tSpan) * SPARK_W, SPARK_H - 1 - (p.v / vMax) * (SPARK_H - 2)]);
  const span = Math.max(1, hi - lo);
  return (
    <svg width={SPARK_W} height={SPARK_H} style={{ display: "block" }}>
      <path
        d={`M0,${SPARK_H} ${xy.map(([x, y]) => `L${x.toFixed(1)},${y.toFixed(1)}`).join(" ")} L${SPARK_W},${SPARK_H} Z`}
        fill="rgba(13, 110, 253, 0.08)"
      />
      {xy.slice(1).map(([x, y], i) => (
        <line
          key={i}
          x1={xy[i][0]}
          y1={xy[i][1]}
          x2={x}
          y2={y}
          stroke={speedColor((((pts[i].v + pts[i + 1].v) / 2) - lo) / span)}
          strokeWidth="2"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
};

const TruckAnalyticsDashboard = ({
  t,
  analyticsData,
  device,
  volumeMode,
  timezoneOffset,
  formatDuration,
  onTimeJump,
  timelineComponent,
  mapData,
  speedTrack,
  mapViewRef,
  onShowAllTrips,
  mapTripId,
  onMapTripChange,
}) => {
  const kpis = analyticsData.kpis || {};
  const averages = analyticsData.averages || {};
  const durations = analyticsData.durations_by_class_sec || {};
  const extraCharts = analyticsData.extra_dash_charts || {};
  const trips = useMemo(() => analyticsData.trips || [], [analyticsData]);
  const idleEvents = analyticsData.idle_events || [];

  const isSolid = volumeMode === "solid";
  const volumeTotal = isSolid ? kpis.solid_volume_total : kpis.volume_total;
  const volumeLabel = isSolid ? "Объем в твердом теле" : "Номинальный объем";

  const [showDetails, setShowDetails] = useState(false);
  const [downtimeModal, setDowntimeModal] = useState({ visible: false, data: null });

  // --- План ковшей на рейс (device.target_buckets), автосохранение через 5 секунд ---
  const [targetBuckets, setTargetBuckets] = useState(device?.target_buckets ?? 5);
  const saveTimerRef = useRef(null);
  const isStaff = localStorage.getItem("is_staff") === "true";

  useEffect(() => () => saveTimerRef.current && clearTimeout(saveTimerRef.current), []);

  useEffect(() => {
    if (device && device.target_buckets !== undefined) setTargetBuckets(device.target_buckets);
  }, [device]);

  const saveTargetBuckets = async (val) => {
    if (!device?.id || !isStaff) return;
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `/api/devices/${device.id}/`,
        { target_buckets: parseFloat(val) || 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("План ковшей сохранен");
    } catch (e) {
      toast.error("Ошибка при сохранении плана");
    }
  };

  // --- Данные графика динамики ---
  const chartData = useMemo(() => {
    const rows = analyticsData.productivity_chart?.data || [];
    return rows.map((d) => ({ ...d, volume_value: Number((isSolid ? d.solid_volume : d.volume) || 0) }));
  }, [analyticsData, isSolid]);

  const avgVolume = useMemo(() => {
    if (!chartData.length) return 0;
    const sum = chartData.reduce((acc, d) => acc + d.volume_value, 0);
    return Number((sum / chartData.length).toFixed(1));
  }, [chartData]);

  const cumulativeData = useMemo(() => {
    let run = 0;
    return [
      {
        id: "Факт (накопительно)",
        color: "#321fdb",
        data: chartData.map((d) => {
          run += d.volume_value;
          return { x: d.time, y: Number(run.toFixed(1)) };
        }),
      },
    ];
  }, [chartData]);

  // --- Составляющие рейса: подряд идущие интервалы без рейсов схлопываем в одну отметку ---
  const tripComponentsData = useMemo(() => {
    const rows = extraCharts.trip_components || [];
    const isEmptyRow = (r) =>
      r.time !== "СРЕДНЕЕ" &&
      (r.trips !== undefined ? r.trips === 0 : PHASE_META.every((p) => !Number(r[p.label])));

    const out = [];
    let gap = null;
    const flush = () => {
      if (!gap) return;
      const [firstDay] = gap.first.split(" ");
      const [lastDay] = gap.last.split(" ");
      const sameDay = gap.first.includes(" ") && firstDay === lastDay;
      const label =
        gap.count === 1 ? gap.first : `${gap.first} – ${sameDay ? gap.last.split(" ").pop() : gap.last}`;
      out.push({ time: label, isGap: true, gapCount: gap.count, trips: 0 });
      gap = null;
    };

    rows.forEach((r) => {
      if (isEmptyRow(r)) {
        if (gap) {
          gap.last = r.time;
          gap.count += 1;
        } else {
          gap = { first: r.time, last: r.time, count: 1 };
        }
      } else {
        flush();
        out.push(r);
      }
    });
    flush();
    return out;
  }, [extraCharts]);

  const gapUnit = analyticsData.productivity_chart?.is_hourly ? " ч" : "";

  // --- Карта: трек всего периода или выбранного рейса ---
  const mapCardRef = useRef(null);
  const mapTrip = useMemo(() => trips.find((tr) => tr.id === mapTripId) || null, [trips, mapTripId]);

  const showTripOnMap = (tripId) => {
    if (onMapTripChange) onMapTripChange(tripId);
    if (mapCardRef.current) mapCardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // --- Границы периода: анализ только по целым рейсам ---
  const rangeAdj = analyticsData.range_adjustment || null;
  const rangeShifted =
    rangeAdj && ((rangeAdj.extended_start_sec || 0) > 0 || (rangeAdj.trimmed_end_sec || 0) > 0);

  // --- Нормы и аномалии рейсов ---
  const [effSettings, setEffSettings] = useState({
    showBars: true,
    showLine: true,
    spbMin: "",
    spbMax: "",
    cycleMin: "",
    cycleMax: "",
  });

  const normBounds = useMemo(() => {
    const pick = (raw, fallback) => {
      const num = Number(raw);
      return raw !== "" && raw !== null && !Number.isNaN(num) ? num : fallback;
    };
    const avgSpb = averages.sec_per_bucket || 0;
    const avgCycle = averages.cycle_sec || 0;
    return {
      spbMin: pick(effSettings.spbMin, avgSpb * 0.7),
      spbMax: pick(effSettings.spbMax, avgSpb * 1.3),
      cycleMin: pick(effSettings.cycleMin, avgCycle * 0.7),
      cycleMax: pick(effSettings.cycleMax, avgCycle * 1.3),
    };
  }, [averages, effSettings]);

  const statsTrips = useMemo(() => trips.filter((tr) => tr.is_complete && tr.in_stats), [trips]);

  const anomalyReasons = useMemo(() => {
    const map = new Map();
    statsTrips.forEach((tr) => {
      const reasons = [];
      if (tr.sec_per_bucket != null && normBounds.spbMin > 0 && tr.sec_per_bucket < normBounds.spbMin) {
        reasons.push(`Быстрая погрузка (<${normBounds.spbMin.toFixed(0)} с/ковш)`);
      }
      if (tr.sec_per_bucket != null && normBounds.spbMax > 0 && tr.sec_per_bucket > normBounds.spbMax) {
        reasons.push(`Медленная погрузка (>${normBounds.spbMax.toFixed(0)} с/ковш)`);
      }
      if (normBounds.cycleMin > 0 && tr.cycle_sec < normBounds.cycleMin) {
        reasons.push(`Короткий рейс (<${(normBounds.cycleMin / 60).toFixed(0)} мин)`);
      }
      if (normBounds.cycleMax > 0 && tr.cycle_sec > normBounds.cycleMax) {
        reasons.push(`Долгий рейс (>${(normBounds.cycleMax / 60).toFixed(0)} мин)`);
      }
      if (reasons.length) map.set(tr.id, reasons);
    });
    return map;
  }, [statsTrips, normBounds]);

  // Данные графика эффективности: по рейсам, а для периода больше 48 часов - по дням
  const effData = useMemo(() => {
    if (!statsTrips.length) return null;
    const rangeMs =
      new Date(analyticsData.date_range?.end).getTime() - new Date(analyticsData.date_range?.start).getTime();
    const isLongPeriod = rangeMs > 48 * 3600 * 1000;

    if (!isLongPeriod) {
      const rows = statsTrips.map((tr, index) => {
        const local = getFakeLocalDate(tr.start, timezoneOffset);
        const label = dayjs(local).format(rangeMs > 24 * 3600 * 1000 ? "DD.MM HH:mm" : "HH:mm");
        return {
          index,
          tripId: tr.id,
          displayLabel: label,
          timeLabel: `${label} (#${tr.id})`,
          title: `Рейс ${tr.id}`,
          durationSec: tr.cycle_sec,
          durationMin: Number((tr.cycle_sec / 60).toFixed(2)),
          buckets: tr.buckets,
          spb: tr.sec_per_bucket,
          isAggregated: false,
          startMs: local.getTime(),
          isAnomaly: anomalyReasons.has(tr.id),
          reasons: anomalyReasons.get(tr.id) || [],
        };
      });
      return { data: rows, isLongPeriod };
    }

    const byDay = new Map();
    statsTrips.forEach((tr) => {
      const local = getFakeLocalDate(tr.start, timezoneOffset);
      const key = dayjs(local).format("DD.MM.YYYY");
      if (!byDay.has(key)) {
        byDay.set(key, { key, cycles: [], spbs: [], buckets: [], startMs: local.getTime(), anomalies: 0 });
      }
      const day = byDay.get(key);
      day.cycles.push(tr.cycle_sec);
      if (tr.sec_per_bucket != null) day.spbs.push(tr.sec_per_bucket);
      day.buckets.push(tr.buckets);
      if (anomalyReasons.has(tr.id)) day.anomalies += 1;
    });

    const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
    const rows = Array.from(byDay.values()).map((day, index) => {
      const durationSec = avg(day.cycles);
      return {
        index,
        tripId: null,
        displayLabel: day.key,
        timeLabel: day.key,
        title: `Рейсов: ${day.cycles.length}`,
        durationSec,
        durationMin: Number((durationSec / 60).toFixed(2)),
        buckets: Math.round(avg(day.buckets)),
        spb: day.spbs.length ? Number(avg(day.spbs).toFixed(1)) : null,
        isAggregated: true,
        startMs: day.startMs,
        isAnomaly: day.anomalies > 0,
        reasons: day.anomalies ? [`Рейсов с отклонением: ${day.anomalies}`] : [],
      };
    });
    return { data: rows, isLongPeriod };
  }, [statsTrips, anomalyReasons, analyticsData, timezoneOffset]);

  const effMaxSpb = useMemo(
    () => (effData ? Math.max(1, ...effData.data.map((d) => d.spb || 0)) * 1.2 : 1),
    [effData]
  );

  // --- Таблица рейсов ---
  const [tripSort, setTripSort] = useState("id_asc");
  const [tripFilters, setTripFilters] = useState({ onlyAnomalies: false, hideOutOfShift: false });

  const visibleTrips = useMemo(() => {
    let rows = [...trips];
    if (tripFilters.hideOutOfShift) rows = rows.filter((tr) => tr.in_stats);
    if (tripFilters.onlyAnomalies) rows = rows.filter((tr) => anomalyReasons.has(tr.id));
    rows.sort((a, b) => {
      switch (tripSort) {
        case "id_desc":
          return b.id - a.id;
        case "buckets_desc":
          return (b.buckets || 0) - (a.buckets || 0);
        case "buckets_asc":
          return (a.buckets || 0) - (b.buckets || 0);
        case "cycle_desc":
          return (b.cycle_sec || 0) - (a.cycle_sec || 0);
        case "cycle_asc":
          return (a.cycle_sec || 0) - (b.cycle_sec || 0);
        default:
          return a.id - b.id;
      }
    });
    return rows;
  }, [trips, tripSort, tripFilters, anomalyReasons]);

  const jumpTo = (isoTime, label) => {
    if (!isoTime || typeof onTimeJump !== "function") return;
    window.scrollTo({ top: 0, behavior: "smooth" });
    onTimeJump({
      exactTime: getFakeLocalDate(isoTime, timezoneOffset).getTime(),
      item: { label },
    });
  };

  const fmtTime = (iso, pattern = "DD.MM HH:mm:ss") =>
    iso ? dayjs(getFakeLocalDate(iso, timezoneOffset)).format(pattern) : "—";

  // Скорость по ходу каждого рейса - из трека с speed_kmh (время трека уже fake-local).
  // Шкала цвета общая для всех рейсов периода, без выбросов (5-й и 95-й процентили)
  const tripSpeed = useMemo(() => {
    const pts = (speedTrack || [])
      .filter((p) => p.speed_kmh != null)
      .map((p) => ({ t: new Date(p.ts).getTime(), v: p.speed_kmh }));
    const values = pts.map((p) => p.v);
    const lo = percentile(values, 5) ?? 0;
    const hi = percentile(values, 95) ?? 1;
    const lowerBound = (t) => {
      let a = 0;
      let b = pts.length;
      while (a < b) {
        const m = (a + b) >> 1;
        if (pts[m].t < t) a = m + 1;
        else b = m;
      }
      return a;
    };
    const series = new Map();
    trips.forEach((tr) => {
      const from = getFakeLocalDate(tr.start, timezoneOffset).getTime();
      const to = getFakeLocalDate(tr.end, timezoneOffset).getTime();
      series.set(tr.id, pts.slice(lowerBound(from), lowerBound(to)));
    });
    return { series, lo, hi: hi > lo ? hi : lo + 1 };
  }, [speedTrack, trips, timezoneOffset]);

  // --- Баланс времени ---
  const timeBalance = useMemo(() => {
    const idle = durationOf(durations, ACT.IDLE);
    const parked = durationOf(durations, ACT.PARKED);
    const noData = Number(durations.no_data || 0);
    return [
      {
        id: "Полезное время",
        label: "Полезное",
        value: Number(kpis.tWork || 0),
        color: "#2eb85c",
        desc: "Движение, погрузка и разгрузка (классы MF, ME, AL, AU).",
      },
      {
        id: "Всего простоев",
        label: "Простои",
        value: idle + parked + noData,
        color: "#e55353",
        desc: "Холостой ход, стоянка и время без данных внутри смены.",
      },
      {
        id: "Холостой ход",
        label: "Двиг. вкл.",
        value: idle,
        color: "#f9b115",
        desc: "Двигатель заведен, машина стоит (класс II).",
      },
      {
        id: "Стоянка",
        label: "Двиг. выкл.",
        value: parked,
        color: "#63c2de",
        desc: "Зажигание выключено (класс IO).",
      },
      {
        id: "Нет данных",
        label: "Нет данных",
        value: noData,
        color: "#8a93a2",
        desc: "Плановое время, за которое нет данных IMU.",
      },
    ];
  }, [durations, kpis]);

  const usefulPct = kpis.engine_hours > 0 ? (kpis.useful_work_mh / kpis.engine_hours) * 100 : 0;
  let usefulColor = "text-success";
  if (usefulPct < 30) usefulColor = "text-danger";
  else if (usefulPct < 60) usefulColor = "text-warning";

  return (
    <div id="truck-analytics-content" style={{ padding: "5px", background: "white" }}>
      {/* ===================== ГРАНИЦЫ ПЕРИОДА ===================== */}
      {rangeAdj && (
        <div className="alert alert-info py-2 small d-flex align-items-start mt-3 mb-0">
          <CIcon icon={cilInfo} className="text-info me-2 flex-shrink-0 mt-1" />
          <div>
            <strong>Анализ ведется только по целым рейсам.</strong> Рейс начинается с концом предыдущей разгрузки
            (начало движения после нее) и заканчивается концом своей разгрузки. Рейс, начатый до выбранного периода и
            закончившийся внутри него, учитывается целиком — начало периода сдвигается на его начало. Рейс, не
            завершенный к концу периода, из анализа исключается — конец периода сдвигается на его начало.
            {rangeShifted && analyticsData.requested_range && (
              <div className="mt-1">
                Проанализирован период{" "}
                <strong>
                  {fmtTime(analyticsData.date_range?.start, "DD.MM HH:mm")} –{" "}
                  {fmtTime(analyticsData.date_range?.end, "DD.MM HH:mm")}
                </strong>{" "}
                (выбрано {fmtTime(analyticsData.requested_range.start, "DD.MM HH:mm")} –{" "}
                {fmtTime(analyticsData.requested_range.end, "DD.MM HH:mm")})
                {rangeAdj.extended_start_sec > 0 && (
                  <>: начало раньше на {formatDuration(rangeAdj.extended_start_sec)}</>
                )}
                {rangeAdj.trimmed_end_sec > 0 && (
                  <>
                    {rangeAdj.extended_start_sec > 0 ? ", " : ": "}
                    незавершенный рейс с {fmtTime(rangeAdj.incomplete_trip_start, "HH:mm")} исключен (конец раньше на{" "}
                    {formatDuration(rangeAdj.trimmed_end_sec)})
                  </>
                )}
                .
              </div>
            )}
            {rangeAdj.no_complete_trips && (
              <div className="mt-1 fw-bold">
                В выбранном периоде нет ни одного завершенного рейса, границы не сдвигались.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ===================== KPI ===================== */}
      <div className="mb-4 mt-3">
        <CRow className="g-3">
          <KpiCard
            title="Рейсы"
            value={kpis.trips_total ?? 0}
            valueClass="text-primary"
            tooltip="Завершенные рейсы: от конца предыдущей разгрузки до конца своей. Незавершенный рейс в конце периода не учитывается."
            sub={
              <div className="d-flex justify-content-center gap-3 small fw-bold m-0">
                {kpis.trips_without_loading > 0 && (
                  <span className="text-warning">Без погрузки: {kpis.trips_without_loading}</span>
                )}
              </div>
            }
          />
          <KpiCard
            title="Ковши"
            value={kpis.buckets_total ?? 0}
            tooltip="Удары ковша по кузову. Одно событие модели = один ковш."
            sub={
              <div className="small fw-bold text-muted m-0">
                На рейс: {fmtNum(kpis.buckets_per_trip_avg)} ({kpis.buckets_per_trip_min ?? 0} /{" "}
                {kpis.buckets_per_trip_max ?? 0})
              </div>
            }
          />
          <KpiCard
            title={volumeLabel}
            value={fmtNum(volumeTotal)}
            unit="м³"
            sub={<div className="small text-muted m-0">Кузов: {fmtNum(kpis.body_volume, 1)} м³</div>}
          />
          <KpiCard title="Масса вывезенного" value={fmtNum(kpis.mass_total_t)} unit="т" />
          <KpiCard
            title="Ср. время рейса"
            value={formatDuration(averages.cycle_sec || 0)}
            valueClass="text-success"
            tooltip="Медиана времени рейса: от конца предыдущей разгрузки до конца своей."
            sub={<div className="small text-muted m-0">Сек/ковш: {fmtNum(averages.sec_per_bucket)}</div>}
          />
          <KpiCard
            title="Полезное время"
            value={
              <>
                {fmtNum(kpis.useful_work_mh)} <span className="text-muted fs-5">/ {fmtNum(kpis.engine_hours)}</span>{" "}
                <span className="fs-6">маш.-ч</span>
              </>
            }
            valueClass="text-primary"
            tooltip="Движение, погрузка и разгрузка относительно моточасов (все, кроме стоянки с выключенным зажиганием)."
            sub={<div className={`${usefulColor} fw-bold m-0`} style={{ fontSize: "0.9rem" }}>{fmtNum(usefulPct)}%</div>}
          />
          <KpiCard
            title="Пробег"
            value={fmtNum(kpis.mileage_total_km, 1)}
            unit="км"
            valueClass="text-info"
            sub={
              <div className="small text-muted m-0">
                Груженый {fmtNum(kpis.mileage_loaded_km, 1)} / пустой {fmtNum(kpis.mileage_empty_km, 1)}
              </div>
            }
          />
          <KpiCard
            title="КИП (пробег с грузом)"
            value={fmtNum(kpis.KIP)}
            unit="%"
            tooltip="Коэффициент использования пробега: пробег груженый / (груженый + пустой)."
          />
          <KpiCard
            title="КИГ (загрузка кузова)"
            value={fmtNum(kpis.KIG)}
            unit="%"
            tooltip={`Среднее ковшей на рейс / план ковшей (${fmtNum(kpis.target_buckets, 0)}).`}
          />
          <KpiCard
            title="Эффективность (OEE)"
            value={fmtNum(kpis.OEE)}
            unit="%"
            valueClass="text-success"
            tooltip={`КТГ ${fmtNum(kpis.KTG)}% × КИВ ${fmtNum(kpis.KIV)}% × КИГ ${fmtNum(kpis.KIG)}%`}
          />
          <KpiCard
            title="Грузооборот"
            value={fmtNum(kpis.cargo_turnover_tkm)}
            unit="т·км"
            tooltip="Сумма по рейсам: масса рейса × пробег груженым."
          />
          {analyticsData.virtual_fuel && (
            <KpiCard
              title="Расход топлива (AI)"
              value={fmtNum(analyticsData.virtual_fuel.total_liters)}
              unit="л"
              valueClass="text-danger"
              tooltip={
                <div className="small text-start">
                  <div>
                    <strong>Спецификация:</strong> {analyticsData.virtual_fuel.spec_name}
                  </div>
                  <div>
                    <strong>Коэфф. (K):</strong> {analyticsData.virtual_fuel.k_factor}
                  </div>
                  <hr className="my-1 border-secondary" />
                  <div>Холостой ход: {analyticsData.virtual_fuel.liters_idle} л</div>
                  <div>Средняя нагрузка: {analyticsData.virtual_fuel.liters_medium} л</div>
                  <div>Высокая нагрузка: {analyticsData.virtual_fuel.liters_high} л</div>
                </div>
              }
              sub={
                <div className="small text-muted m-0">
                  {fmtNum(analyticsData.virtual_fuel.liters_per_trip, 1)} л/рейс
                </div>
              }
            />
          )}
        </CRow>
      </div>

      {/* ===================== ДИНАМИКА РАБОТЫ ===================== */}
      {chartData.length > 0 && (
        <>
          <svg width="0" height="0">
            <defs>
              <pattern id="truck-diagonal-stripes" width="8" height="8" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                <rect width="8" height="8" fill="transparent" />
                <line x1="0" y1="0" x2="0" y2="8" stroke="#dcdcdc" strokeWidth="4" />
              </pattern>
            </defs>
          </svg>

          <CRow className="mb-4">
            <CCol xs={12}>
              <CCard className="shadow-sm border-0">
                <CCardHeader className="bg-white fw-bold d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <span>Динамика работы ({analyticsData.productivity_chart?.period_label})</span>
                  <div className="d-flex gap-2 flex-wrap align-items-center">
                    <CBadge color="success" shape="rounded-pill">Объем (м³)</CBadge>
                    <CBadge color="primary" shape="rounded-pill">Рейсы (шт)</CBadge>
                  </div>
                </CCardHeader>
                <CCardBody className="p-0">
                  <div style={{ width: "100%", height: "420px", paddingTop: "10px" }}>
                    <ZoomableNivoWrapper dataLength={chartData.length} minWindow={5}>
                      {(sIdx, eIdx) => {
                        const sliced = chartData.slice(sIdx, eIdx);
                        const maxTrips = Math.max(1, ...sliced.map((d) => Number(d.trips) || 0));

                        const TripsLineLayer = ({ xScale, innerHeight }) => {
                          const points = [];
                          sliced.forEach((d) => {
                            const xVal = xScale(d.time);
                            if (xVal === undefined) return;
                            const val = Number(d.trips) || 0;
                            if (val <= 0) return;
                            points.push({
                              x: xVal + xScale.bandwidth() / 2,
                              y: innerHeight - (val / maxTrips) * (innerHeight * 0.85),
                              val,
                            });
                          });
                          if (!points.length) return null;
                          const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x},${p.y}`).join(" ");

                          return (
                            <g>
                              <path d={path} fill="none" stroke="#0d6efd" strokeWidth={3} />
                              {points.map((p, i) => (
                                <g key={`trips-pt-${i}`}>
                                  <circle cx={p.x} cy={p.y} r={5} fill="#0d6efd" stroke="#fff" strokeWidth={2} />
                                  <text
                                    x={p.x}
                                    y={p.y - 12}
                                    textAnchor="middle"
                                    fill="#0d6efd"
                                    fontSize="11"
                                    fontWeight="bold"
                                    style={{ textShadow: "1px 1px 2px #fff, -1px -1px 2px #fff" }}
                                  >
                                    {p.val}
                                  </text>
                                </g>
                              ))}
                            </g>
                          );
                        };

                        return (
                          <ResponsiveBar
                            data={sliced}
                            keys={["volume_value"]}
                            indexBy="time"
                            margin={{ top: 60, right: 30, bottom: 80, left: 60 }}
                            padding={0.3}
                            valueScale={{ type: "linear" }}
                            indexScale={{ type: "band", round: true }}
                            colors={["#2eb85c"]}
                            borderRadius={2}
                            axisTop={null}
                            axisRight={null}
                            axisBottom={{
                              tickSize: 5,
                              tickPadding: 5,
                              tickRotation: -45,
                              tickValues:
                                sliced.length > 12
                                  ? sliced
                                      .filter((_, i) => i % Math.ceil(sliced.length / 10) === 0)
                                      .map((d) => d.time)
                                  : undefined,
                            }}
                            axisLeft={{
                              tickSize: 5,
                              tickPadding: 5,
                              legend: "Объем (м³)",
                              legendPosition: "middle",
                              legendOffset: -50,
                            }}
                            labelSkipWidth={16}
                            labelSkipHeight={16}
                            labelTextColor="#ffffff"
                            layers={[
                              "grid",
                              "axes",
                              ({ xScale, innerHeight }) =>
                                sliced
                                  .filter((d) => d.is_break)
                                  .map((d, i) => {
                                    const x = xScale(d.time);
                                    if (x === undefined) return null;
                                    const w = xScale.bandwidth();
                                    return (
                                      <g key={`break-${i}`}>
                                        <rect x={x} y={0} width={w} height={innerHeight} fill="url(#truck-diagonal-stripes)" />
                                        <text
                                          x={x + w / 2}
                                          y={-5}
                                          textAnchor="start"
                                          transform={`rotate(-45 ${x + w / 2} -5)`}
                                          fill="#6c757d"
                                          fontSize="11"
                                          fontWeight="bold"
                                        >
                                          {d.break_desc || "Перерыв"}
                                        </text>
                                      </g>
                                    );
                                  }),
                              "bars",
                              TripsLineLayer,
                              "markers",
                            ]}
                            markers={[
                              {
                                axis: "y",
                                value: avgVolume,
                                lineStyle: { stroke: "#321fdb", strokeWidth: 2, strokeDasharray: "4 4" },
                                legend: `Ср. факт: ${avgVolume} м³`,
                                legendOrientation: "horizontal",
                                legendPosition: "top-right",
                                textStyle: { fill: "#321fdb", fontSize: 12, fontWeight: "bold" },
                              },
                            ]}
                            tooltip={({ data }) => (
                              <div
                                style={{
                                  padding: "10px",
                                  background: "white",
                                  border: "1px solid #ccc",
                                  borderRadius: "6px",
                                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                                  minWidth: "220px",
                                }}
                              >
                                <div className="fw-bold mb-2 border-bottom pb-1 text-center">
                                  {data.time} {data.is_break && <span className="text-muted fw-normal">(Перерыв)</span>}
                                </div>
                                <div className="d-flex justify-content-between small">
                                  <span>Объем:</span>
                                  <strong className="text-success ms-2">{fmtNum(data.volume_value)} м³</strong>
                                </div>
                                <div className="d-flex justify-content-between small">
                                  <span>Масса:</span>
                                  <strong className="ms-2">{fmtNum(data.mass_t)} т</strong>
                                </div>
                                <div className="d-flex justify-content-between small">
                                  <span>Рейсов:</span>
                                  <strong className="text-primary ms-2">{data.trips} шт.</strong>
                                </div>
                                <div className="d-flex justify-content-between small">
                                  <span>Ковшей:</span>
                                  <strong className="ms-2">{data.buckets} шт.</strong>
                                </div>
                                <div className="d-flex justify-content-between small border-top pt-1 mt-1">
                                  <span>Ср. время рейса:</span>
                                  <strong className="ms-2">{formatDuration(data.avg_cycle_sec || 0)}</strong>
                                </div>
                                <div className="d-flex justify-content-between small">
                                  <span>Простой:</span>
                                  <strong className="text-danger ms-2">
                                    {Math.round((data.downtime_sec || 0) / 60)} мин.
                                  </strong>
                                </div>
                              </div>
                            )}
                          />
                        );
                      }}
                    </ZoomableNivoWrapper>
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>

          {/* ===================== НАКОПИТЕЛЬНЫЙ ИТОГ | БАЛАНС ВРЕМЕНИ ===================== */}
          <CRow className="mb-4 g-3">
            <CCol xs={12} lg={7}>
              <CCard className="shadow-sm border-0 h-100">
                <CCardHeader className="bg-white fw-bold">Накопительный итог объема</CCardHeader>
                <CCardBody className="p-0">
                  <div style={{ height: "350px", width: "100%" }}>
                    <ZoomableNivoWrapper dataLength={cumulativeData[0]?.data.length || 0} minWindow={2}>
                      {(sIdx, eIdx) => {
                        const sliced = cumulativeData.map((s) => ({ ...s, data: s.data.slice(sIdx, eIdx) }));
                        return (
                          <ResponsiveLine
                            data={sliced}
                            margin={{ top: 30, right: 30, bottom: 80, left: 60 }}
                            xScale={{ type: "point" }}
                            yScale={{ type: "linear", min: 0, max: "auto" }}
                            curve="monotoneX"
                            axisTop={null}
                            axisRight={null}
                            axisBottom={{
                              tickSize: 5,
                              tickPadding: 5,
                              tickRotation: -45,
                              tickValues:
                                sliced[0]?.data.length > 12
                                  ? sliced[0].data
                                      .filter((_, i) => i % Math.ceil(sliced[0].data.length / 10) === 0)
                                      .map((d) => d.x)
                                  : undefined,
                            }}
                            axisLeft={{
                              tickSize: 5,
                              tickPadding: 5,
                              legend: "Накопленный объем (м³)",
                              legendPosition: "middle",
                              legendOffset: -50,
                            }}
                            colors={["#321fdb"]}
                            lineWidth={3}
                            enablePoints={false}
                            enableArea
                            areaOpacity={0.15}
                            useMesh
                            tooltip={({ point }) => (
                              <div
                                style={{
                                  padding: "8px 12px",
                                  background: "white",
                                  border: "1px solid #321fdb",
                                  borderRadius: "4px",
                                }}
                              >
                                {point.data.xFormatted}: <strong>{point.data.yFormatted} м³</strong>
                              </div>
                            )}
                          />
                        );
                      }}
                    </ZoomableNivoWrapper>
                  </div>
                </CCardBody>
              </CCard>
            </CCol>

            <CCol xs={12} lg={5}>
              <CCard className="shadow-sm border-0 h-100">
                <CCardHeader className="bg-white fw-bold border-bottom-0 text-center">Баланс времени работы</CCardHeader>
                <CCardBody className="p-0">
                  <div style={{ height: "350px" }}>
                    {timeBalance.every((d) => d.value === 0) ? (
                      <div className="d-flex h-100 align-items-center justify-content-center text-muted">
                        {t("dashboard.no_data", "Нет данных")}
                      </div>
                    ) : (
                      <ResponsiveBar
                        data={timeBalance}
                        keys={["value"]}
                        indexBy="label"
                        margin={{ top: 30, right: 20, bottom: 40, left: 60 }}
                        padding={0.3}
                        valueScale={{ type: "linear" }}
                        colors={({ data }) => data.color}
                        colorBy="indexValue"
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: 0 }}
                        axisLeft={{
                          tickSize: 5,
                          tickPadding: 5,
                          legend: "Время (часы)",
                          legendPosition: "middle",
                          legendOffset: -50,
                          format: (v) => `${(v / 3600).toFixed(1)}ч`,
                        }}
                        enableLabel={false}
                        tooltip={({ data }) => (
                          <div
                            style={{
                              padding: "12px",
                              background: "white",
                              border: `1px solid ${data.color}`,
                              borderRadius: "6px",
                              boxShadow: "0 4px 15px rgba(0,0,0,0.15)",
                              maxWidth: "260px",
                              whiteSpace: "normal",
                            }}
                          >
                            <div
                              style={{
                                color: data.color,
                                fontWeight: "bold",
                                borderBottom: "1px solid #eee",
                                paddingBottom: "6px",
                                marginBottom: "8px",
                              }}
                            >
                              {data.id}:<br />
                              <span className="text-dark fs-5">{formatDuration(data.value)}</span>
                            </div>
                            <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>{data.desc}</div>
                          </div>
                        )}
                      />
                    )}
                  </div>
                  <div className="p-3 text-center border-top">
                    <CButton
                      color="danger"
                      variant="outline"
                      size="sm"
                      className="fw-bold shadow-sm"
                      onClick={() => setDowntimeModal({ visible: true, data: null })}
                    >
                      <CIcon icon={cilWarning} className="me-1" /> Журнал простоев (Классифицировать)
                    </CButton>
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        </>
      )}

      {/* ===================== СОСТАВЛЯЮЩИЕ РЕЙСА ===================== */}
      {tripComponentsData.length > 0 && (
        <CRow className="mb-4">
          <CCol xs={12}>
            <CCard className="shadow-sm border-0">
              <CCardHeader className="bg-white fw-bold d-flex justify-content-between align-items-center">
                <span>Составляющие рейса (медиана, сек)</span>
                <span className="text-muted fw-normal small">
                  Штриховка — интервалы без рейсов, подряд идущие собраны в одну отметку
                </span>
              </CCardHeader>
              <CCardBody>
                <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
                  <div
                    style={{
                      height: "350px",
                      width: `${Math.max(100, tripComponentsData.length * 5)}%`,
                      minWidth: "100%",
                    }}
                  >
                    <ResponsiveBar
                      data={tripComponentsData}
                      keys={PHASE_META.map((p) => p.label)}
                      indexBy="time"
                      margin={{ top: 20, right: 190, bottom: 70, left: 60 }}
                      layers={[
                        "grid",
                        "axes",
                        ({ xScale, innerHeight }) => (
                          <g>
                            {tripComponentsData
                              .filter((d) => d.isGap)
                              .map((d) => {
                                const x = xScale(d.time);
                                if (x === undefined) return null;
                                const w = xScale.bandwidth();
                                const cx = x + w / 2;
                                const cy = innerHeight / 2;
                                const caption = d.gapCount > 1 ? `нет рейсов · ${d.gapCount}${gapUnit}` : "нет рейсов";
                                return (
                                  <g key={`gap-${d.time}`}>
                                    <title>
                                      {d.gapCount > 1
                                        ? `${d.time}: рейсов не было (${d.gapCount} интервалов подряд)`
                                        : `${d.time}: рейсов не было`}
                                    </title>
                                    <rect
                                      x={x}
                                      y={0}
                                      width={w}
                                      height={innerHeight}
                                      fill="url(#truck-diagonal-stripes)"
                                      stroke="#dee2e6"
                                    />
                                    <text
                                      x={cx}
                                      y={cy}
                                      transform={`rotate(-90 ${cx} ${cy})`}
                                      textAnchor="middle"
                                      dominantBaseline="middle"
                                      fill="#6c757d"
                                      fontSize="11"
                                      fontWeight="bold"
                                    >
                                      {caption}
                                    </text>
                                  </g>
                                );
                              })}
                          </g>
                        ),
                        "bars",
                        "markers",
                        "legends",
                      ]}
                      padding={0.3}
                      colors={PHASE_META.map((p) => p.color)}
                      valueScale={{ type: "linear" }}
                      indexScale={{ type: "band", round: true }}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{ tickSize: 5, tickPadding: 5, tickRotation: -45 }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        legend: "Секунды",
                        legendPosition: "middle",
                        legendOffset: -45,
                      }}
                      legends={[
                        {
                          dataFrom: "keys",
                          anchor: "bottom-right",
                          direction: "column",
                          justify: false,
                          translateX: 180,
                          translateY: 0,
                          itemsSpacing: 8,
                          itemWidth: 170,
                          itemHeight: 20,
                          symbolSize: 14,
                        },
                      ]}
                      tooltip={({ id, value, color, indexValue }) => (
                        <div style={{ padding: "8px", background: "white", border: `1px solid ${color}`, borderRadius: "4px" }}>
                          <strong>{indexValue}</strong>
                          <br />
                          {id}: <strong style={{ color }}>{formatDuration(value)}</strong>
                        </div>
                      )}
                    />
                  </div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}

      {/* ===================== КОВШЕЙ НА РЕЙС | ПАРЕТО ===================== */}
      <CRow className="mb-4 g-3">
        <CCol xs={12} lg={6}>
          <CCard className="shadow-sm border-0 h-100">
            <CCardHeader className="bg-white fw-bold d-flex justify-content-between align-items-center">
              <span>Ковшей на рейс</span>
              <div className="d-flex align-items-center">
                <label className="me-2 fw-bold small text-muted mb-0" htmlFor="truckTargetBuckets">
                  План (ковшей):
                </label>
                <input
                  id="truckTargetBuckets"
                  type="number"
                  className="form-control form-control-sm text-center fw-bold text-danger"
                  style={{ width: "70px" }}
                  value={targetBuckets}
                  step="0.1"
                  min="0"
                  disabled={!isStaff}
                  title={
                    isStaff
                      ? "Сохранится автоматически через 5 сек после ввода"
                      : "Изменение доступно только администратору"
                  }
                  onChange={(e) => {
                    const val = Number(e.target.value) < 0 ? 0 : e.target.value;
                    setTargetBuckets(val);
                    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
                    saveTimerRef.current = setTimeout(() => saveTargetBuckets(val), 5000);
                  }}
                />
              </div>
            </CCardHeader>
            <CCardBody>
              {(() => {
                const bucketsData = extraCharts.buckets_per_trip || [];
                const hasData = bucketsData.some((d) => d.avg_buckets !== null && d.avg_buckets !== undefined);
                if (!hasData) {
                  return (
                    <div
                      style={{ height: "320px" }}
                      className="d-flex align-items-center justify-content-center text-muted bg-light border rounded"
                    >
                      Нет данных о погрузках за выбранный период
                    </div>
                  );
                }
                return (
                  <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
                    <div
                      style={{
                        height: "320px",
                        width: `${Math.max(100, bucketsData.length * 4)}%`,
                        minWidth: "100%",
                      }}
                    >
                      <ResponsiveLine
                        data={[
                          {
                            id: "Факт (ковшей на рейс)",
                            color: "#321fdb",
                            data: bucketsData.map((d) => ({ x: d.time, y: d.avg_buckets })),
                          },
                        ]}
                        margin={{ top: 20, right: 30, bottom: 70, left: 50 }}
                        xScale={{ type: "point" }}
                        yScale={{ type: "linear", min: 0, max: "auto" }}
                        curve="monotoneX"
                        axisBottom={{ tickRotation: -45 }}
                        axisLeft={{ legend: "Ковшей", legendPosition: "middle", legendOffset: -40 }}
                        colors={["#321fdb"]}
                        lineWidth={3}
                        pointSize={8}
                        useMesh
                        markers={[
                          {
                            axis: "y",
                            value: Number(targetBuckets) || 0,
                            lineStyle: { stroke: "#e55353", strokeWidth: 2, strokeDasharray: "4 4" },
                            legend: "План",
                            legendOrientation: "horizontal",
                            textStyle: { fill: "#e55353", fontWeight: "bold" },
                          },
                        ]}
                      />
                    </div>
                  </div>
                );
              })()}
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xs={12} lg={6}>
          <CCard className="shadow-sm border-0 h-100">
            <CCardHeader className="bg-white fw-bold">Распределение ковшей по рейсам</CCardHeader>
            <CCardBody>
              <div style={{ height: "320px", width: "100%" }}>
                {(() => {
                  const paretoData = extraCharts.pareto_data || [];
                  if (!paretoData.length) {
                    return (
                      <div className="d-flex h-100 align-items-center justify-content-center text-muted bg-light border rounded">
                        Нет завершенных рейсов с погрузкой
                      </div>
                    );
                  }
                  const maxFreq = Math.max(1, ...paretoData.map((d) => d.frequency));

                  const ParetoLineLayer = ({ xScale, innerHeight }) => {
                    const points = paretoData
                      .map((d) => {
                        const xVal = xScale(d.buckets);
                        if (xVal === undefined) return null;
                        return {
                          x: xVal + xScale.bandwidth() / 2,
                          y: innerHeight - (d.cumulative_pct / 100) * innerHeight,
                          pct: d.cumulative_pct,
                        };
                      })
                      .filter(Boolean);
                    if (!points.length) return null;
                    const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x},${p.y}`).join(" ");

                    return (
                      <g>
                        <path d={path} fill="none" stroke="#e55353" strokeWidth={3} />
                        {points.map((p, i) => (
                          <g key={`pareto-pt-${i}`}>
                            <circle cx={p.x} cy={p.y} r={4} fill="#e55353" stroke="#fff" strokeWidth={2} />
                            <text x={p.x} y={p.y - 10} textAnchor="middle" fill="#e55353" fontSize="11" fontWeight="bold">
                              {p.pct}
                            </text>
                          </g>
                        ))}
                      </g>
                    );
                  };

                  return (
                    <ResponsiveBar
                      data={paretoData}
                      keys={["frequency"]}
                      indexBy="buckets"
                      margin={{ top: 30, right: 60, bottom: 50, left: 50 }}
                      padding={0.3}
                      colors={["#321fdb"]}
                      axisTop={null}
                      axisRight={{
                        tickValues: 5,
                        tickFormat: (v) => `${Math.round((v / maxFreq) * 100)}%`,
                        legend: "Накопительный %",
                        legendPosition: "middle",
                        legendOffset: 45,
                      }}
                      axisBottom={{ legend: "Ковшей в рейсе", legendPosition: "middle", legendOffset: 40 }}
                      axisLeft={{ legend: "Рейсов", legendPosition: "middle", legendOffset: -40 }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      labelTextColor="#ffffff"
                      layers={["grid", "axes", "bars", ParetoLineLayer, "markers"]}
                      tooltip={({ value, indexValue }) => (
                        <div style={{ padding: "8px", background: "white", border: "1px solid #ccc", borderRadius: "4px" }}>
                          Рейсов по <strong>{indexValue} ковшей</strong>: {value} шт.
                        </div>
                      )}
                    />
                  );
                })()}
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* ===================== ЭФФЕКТИВНОСТЬ РЕЙСОВ ===================== */}
      {effData && effData.data.length > 0 && (
        <CRow className="mb-4">
          <CCol xs={12}>
            <CCard className="shadow-sm border-0">
              <CCardHeader className="bg-white py-3 d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-3">
                <div className="d-flex align-items-center">
                  <span className="fw-bold fs-6">Эффективность рейсов (отклонения)</span>
                  <SafeTooltip content="Оставьте поля пустыми, чтобы система сама нашла отклонения: ±30% от медианы по периоду.">
                    <CIcon icon={cilInfo} className="text-info ms-2" style={{ cursor: "help" }} />
                  </SafeTooltip>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-3">
                  <div className="d-flex flex-wrap align-items-center gap-2 bg-light px-2 py-2 rounded border">
                    <span className="small text-muted fw-bold me-1">Нормы (от-до):</span>
                    <div className="input-group input-group-sm" style={{ width: "170px" }}>
                      <span className="input-group-text bg-white small" title="Секунд на один ковш">Ковш</span>
                      <input
                        type="number"
                        className="form-control text-center px-1 fw-bold text-primary"
                        placeholder={normBounds.spbMin.toFixed(0)}
                        value={effSettings.spbMin}
                        onChange={(e) => setEffSettings((p) => ({ ...p, spbMin: e.target.value }))}
                      />
                      <input
                        type="number"
                        className="form-control text-center px-1 fw-bold text-danger"
                        placeholder={normBounds.spbMax.toFixed(0)}
                        value={effSettings.spbMax}
                        onChange={(e) => setEffSettings((p) => ({ ...p, spbMax: e.target.value }))}
                      />
                    </div>
                    <div className="input-group input-group-sm" style={{ width: "170px" }}>
                      <span className="input-group-text bg-white small" title="Время рейса, секунды">Рейс</span>
                      <input
                        type="number"
                        className="form-control text-center px-1 fw-bold text-primary"
                        placeholder={normBounds.cycleMin.toFixed(0)}
                        value={effSettings.cycleMin}
                        onChange={(e) => setEffSettings((p) => ({ ...p, cycleMin: e.target.value }))}
                      />
                      <input
                        type="number"
                        className="form-control text-center px-1 fw-bold text-danger"
                        placeholder={normBounds.cycleMax.toFixed(0)}
                        value={effSettings.cycleMax}
                        onChange={(e) => setEffSettings((p) => ({ ...p, cycleMax: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-1">
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="truckShowLine"
                        checked={effSettings.showLine}
                        onChange={(e) => setEffSettings((p) => ({ ...p, showLine: e.target.checked }))}
                      />
                      <label className="form-check-label small fw-bold" htmlFor="truckShowLine">
                        Сек/ковш (линия)
                      </label>
                    </div>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="truckShowBars"
                        checked={effSettings.showBars}
                        onChange={(e) => setEffSettings((p) => ({ ...p, showBars: e.target.checked }))}
                      />
                      <label className="form-check-label small fw-bold" htmlFor="truckShowBars">
                        Время рейса (столбцы)
                      </label>
                    </div>
                  </div>
                </div>
              </CCardHeader>

              <CCardBody className="p-3">
                {effData.isLongPeriod && (
                  <div className="alert alert-info py-2 d-flex align-items-center mb-3">
                    <CIcon icon={cilInfo} className="text-info flex-shrink-0 me-3" size="xl" />
                    <span className="small">
                      <strong>Период больше 48 часов.</strong> Данные на графике усреднены по дням, отдельные отклонения
                      сглаживаются. Для разбора конкретных рейсов выберите период короче двух суток или смотрите таблицу
                      рейсов ниже.
                    </span>
                  </div>
                )}

                {effData.data.some((d) => d.isAnomaly) ? (
                  <div className="alert alert-danger py-2 d-flex align-items-center flex-wrap gap-2 mb-3">
                    <CIcon icon={cilWarning} className="text-danger flex-shrink-0" />
                    <span className="fw-bold small me-2">
                      Отклонений: {effData.data.filter((d) => d.isAnomaly).length}
                    </span>
                    {effData.data
                      .filter((d) => d.isAnomaly)
                      .slice(0, 8)
                      .map((d, i) => (
                        <CBadge
                          key={i}
                          color="danger"
                          shape="rounded-pill"
                          style={{ cursor: d.isAggregated ? "default" : "pointer" }}
                          title={d.reasons.join("\n")}
                          onClick={() =>
                            !d.isAggregated
                            && jumpTo(trips.find((tr) => tr.id === d.tripId)?.start, `Рейс ${d.tripId}`)
                          }
                        >
                          {d.displayLabel}
                        </CBadge>
                      ))}
                  </div>
                ) : (
                  <div className="alert alert-success py-2 mb-3">
                    <span className="fw-bold small">Отклонений не выявлено, рейсы в пределах норм.</span>
                  </div>
                )}

                <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden" }}>
                  <div
                    style={{
                      height: "360px",
                      width: `${Math.max(100, effData.data.length * 6)}%`,
                      minWidth: "100%",
                    }}
                  >
                    <ResponsiveBar
                      data={effData.data}
                      keys={["durationMin"]}
                      indexBy="timeLabel"
                      margin={{ top: 30, right: 30, bottom: 80, left: 55 }}
                      padding={0.4}
                      colors={({ data }) => {
                        if (!effSettings.showBars) return "transparent";
                        return data.isAnomaly ? "#e55353" : "rgba(50, 31, 219, 0.6)";
                      }}
                      borderRadius={4}
                      axisTop={null}
                      axisRight={null}
                      axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: -45,
                        legend: effData.isLongPeriod ? "Дни" : "Рейсы по порядку",
                        legendPosition: "middle",
                        legendOffset: 65,
                        format: (v) => (typeof v === "string" && v.includes(" (#") ? v.split(" (#")[0] : v),
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        legend: "Время рейса (мин)",
                        legendPosition: "middle",
                        legendOffset: -45,
                      }}
                      enableLabel={false}
                      layers={[
                        "grid",
                        "axes",
                        "bars",
                        ({ bars, innerHeight, width }) => {
                          if (!effSettings.showLine) return null;
                          const yOf = (spb) => innerHeight - (spb / effMaxSpb) * innerHeight;
                          const points = bars
                            .filter((bar) => bar.data.data.spb != null)
                            .map((bar) => ({
                              x: bar.x + bar.width / 2,
                              y: yOf(bar.data.data.spb),
                              d: bar.data.data,
                            }));
                          const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x},${p.y}`).join(" ");

                          return (
                            <g>
                              <line
                                x1={0}
                                x2={width}
                                y1={yOf(normBounds.spbMin)}
                                y2={yOf(normBounds.spbMin)}
                                stroke="#2eb85c"
                                strokeWidth={1}
                                strokeDasharray="4 4"
                              />
                              <line
                                x1={0}
                                x2={width}
                                y1={yOf(normBounds.spbMax)}
                                y2={yOf(normBounds.spbMax)}
                                stroke="#e55353"
                                strokeWidth={1}
                                strokeDasharray="4 4"
                              />
                              <text x={4} y={yOf(normBounds.spbMin) - 4} fontSize="10" fill="#2eb85c" fontWeight="bold">
                                мин {normBounds.spbMin.toFixed(0)} с/ковш
                              </text>
                              <text x={4} y={yOf(normBounds.spbMax) - 4} fontSize="10" fill="#e55353" fontWeight="bold">
                                макс {normBounds.spbMax.toFixed(0)} с/ковш
                              </text>
                              {points.length > 1 && <path d={path} fill="none" stroke="#f9b115" strokeWidth={3} />}
                              {points.map((p, i) => (
                                <g key={`spb-${i}`}>
                                  <circle
                                    cx={p.x}
                                    cy={p.y}
                                    r={p.d.isAnomaly ? 6 : 4}
                                    fill={p.d.isAnomaly ? "#e55353" : "#f9b115"}
                                    stroke="#fff"
                                    strokeWidth={2}
                                  />
                                  <text
                                    x={p.x}
                                    y={p.y - 12}
                                    textAnchor="middle"
                                    fontSize="11"
                                    fontWeight="bold"
                                    fill={p.d.isAnomaly ? "#e55353" : "#495057"}
                                    style={{ textShadow: "1px 1px 2px #fff, -1px -1px 2px #fff" }}
                                  >
                                    {fmtNum(p.d.spb)}с
                                  </text>
                                </g>
                              ))}
                            </g>
                          );
                        },
                      ]}
                      onClick={(node) => {
                        const row = node.data;
                        if (row.isAggregated || !row.tripId) return;
                        const trip = trips.find((tr) => tr.id === row.tripId);
                        jumpTo(trip?.start, `Рейс ${row.tripId}`);
                      }}
                      theme={{ tooltip: { container: { cursor: "pointer" } } }}
                      tooltip={({ data }) => (
                        <div
                          style={{
                            padding: "10px",
                            background: "white",
                            border: `2px solid ${data.isAnomaly ? "#e55353" : "#2eb85c"}`,
                            borderRadius: "6px",
                            boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                          }}
                        >
                          <div className="fw-bold border-bottom pb-1 mb-2 text-center text-dark">
                            {data.displayLabel} <span className="text-muted fw-normal">| {data.title}</span>
                          </div>
                          <div className="small d-flex justify-content-between mb-1">
                            <span className="text-muted">{data.isAggregated ? "Ср. время рейса:" : "Время рейса:"}</span>
                            <strong className="ms-3">{formatDuration(data.durationSec)}</strong>
                          </div>
                          <div className="small d-flex justify-content-between mb-1">
                            <span className="text-muted">{data.isAggregated ? "Ср. ковшей:" : "Ковшей:"}</span>
                            <strong className="ms-3">{data.buckets} шт.</strong>
                          </div>
                          <div className="small d-flex justify-content-between mt-2 pt-2 border-top">
                            <span className="fw-bold">Секунд на ковш:</span>
                            <strong className={`ms-3 fs-6 ${data.isAnomaly ? "text-danger" : "text-success"}`}>
                              {fmtNum(data.spb)}
                            </strong>
                          </div>
                          {data.reasons.length > 0 && (
                            <div className="mt-2 pt-2 border-top">
                              {data.reasons.map((r, idx) => (
                                <div key={idx} className="text-danger fw-bold" style={{ fontSize: "0.7rem" }}>
                                  - {r}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      )}

      {/* ===================== ТАЙМЛАЙН ===================== */}
      <div className="mb-4">{timelineComponent}</div>

      {/* ===================== КАРТА ===================== */}
      <div ref={mapCardRef} className="mb-4">
        <CCard className="shadow-sm border-0">
          <CCardHeader className="bg-white py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
            <span className="fw-bold d-flex align-items-center">
              <CIcon icon={cilMap} className="text-primary me-2" />
              {mapTrip ? `Трек рейса ${mapTrip.id}` : "Трек за период"}
            </span>
            <div className="d-flex flex-wrap align-items-center gap-2">
              {mapTrip && (
                <span className="small text-muted">
                  {fmtTime(mapTrip.start, "HH:mm")}–{fmtTime(mapTrip.end, "HH:mm")} · {mapTrip.buckets} ковш. · груженый{" "}
                  {fmtNum(mapTrip.distance_loaded_km, 1)} км / пустой {fmtNum(mapTrip.distance_empty_km, 1)} км
                </span>
              )}
              <CFormSelect
                size="sm"
                style={{ minWidth: "240px", cursor: "pointer" }}
                value={mapTripId ?? ""}
                onChange={(e) => onMapTripChange && onMapTripChange(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Весь период</option>
                {trips.map((tr) => (
                  <option key={tr.id} value={tr.id}>
                    Рейс {tr.id} · {fmtTime(tr.start, "HH:mm")}–{fmtTime(tr.end, "HH:mm")} · {tr.buckets} ковш.
                  </option>
                ))}
              </CFormSelect>
            </div>
          </CCardHeader>
          <CCardBody className="p-2">
            {/* Карта не пересоздается при смене рейса: тип тайлов и вид остаются пользовательскими.
                Точки погрузки/разгрузки: клик - перемотка видео на этот момент */}
            <TruckMap
              track={mapData?.track || []}
              loadPoints={mapData?.loadPoints || []}
              unloadPoints={mapData?.unloadPoints || []}
              height="max(560px, 70vh)"
              viewState={mapViewRef}
              onPointClick={onTimeJump}
              onShowAll={mapTripId != null ? onShowAllTrips : null}
            />
          </CCardBody>
        </CCard>
      </div>

      {/* ===================== СТРУКТУРА РЕЙСА | ПРОБЕГ ===================== */}
      <CRow className="g-4 mb-4">
        <CCol md={6}>
          <CCard className="h-100 shadow-sm border-0">
            <CCardHeader className="bg-light border-bottom-0 py-3 fw-bold fs-6 text-dark">Структура рейса</CCardHeader>
            <CCardBody className="bg-white">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <SafeTooltip content="Медиана времени рейса: от конца предыдущей разгрузки до конца своей, без стоянки с выключенным зажиганием.">
                  <span
                    className="fw-bold text-dark small text-uppercase"
                    style={{ borderBottom: "1px dashed #999", cursor: "help" }}
                  >
                    Ср. время рейса:
                  </span>
                </SafeTooltip>
                <strong className="fs-5 text-dark">{formatDuration(averages.cycle_sec || 0)}</strong>
              </div>

              <div className="mb-3">
                <PhaseBar
                  phases={PHASE_META.reduce((acc, p) => ({ ...acc, [p.key]: averages[`${p.key}_sec`] }), {})}
                  formatDuration={formatDuration}
                />
              </div>

              <div className="bg-light rounded p-3">
                {PHASE_META.map((p) => (
                  <div key={p.key} className="d-flex justify-content-between text-muted small mb-2">
                    <span className="d-flex align-items-center">
                      <span
                        className="d-inline-block rounded-circle me-2 shadow-sm"
                        style={{ width: "10px", height: "10px", backgroundColor: p.color }}
                      />
                      {p.label}:
                    </span>
                    <span className="fw-bold text-dark">{formatDuration(averages[`${p.key}_sec`] || 0)}</span>
                  </div>
                ))}
                <div className="d-flex justify-content-between small mt-3 pt-2 border-top">
                  <span className="text-muted fw-bold">
                    Ожидание под погрузкой (среднее)
                    {averages.queue_trips_pct != null && (
                      <span className="fw-normal"> · в очереди {fmtNum(averages.queue_trips_pct, 0)}% рейсов</span>
                    )}
                    :
                  </span>
                  <span className="fw-bold text-dark">{formatDuration(averages.wait_before_load_sec || 0)}</span>
                </div>
                <div className="d-flex justify-content-between small mt-2">
                  <span className="text-muted fw-bold">Секунд на ковш:</span>
                  <span className="fw-bold text-dark">{fmtNum(averages.sec_per_bucket)}</span>
                </div>
              </div>
            </CCardBody>
          </CCard>
        </CCol>

        <CCol md={6}>
          <CCard className="h-100 shadow-sm border-0">
            <CCardHeader className="bg-light border-bottom-0 py-3 fw-bold fs-6 text-dark">Пробег и скорость</CCardHeader>
            <CCardBody className="bg-white p-0">
              <CListGroup flush>
                <CListGroupItem className="d-flex justify-content-between">
                  <span className="text-muted">Пробег всего</span>
                  <strong>{fmtNum(kpis.mileage_total_km, 1)} км</strong>
                </CListGroupItem>
                <CListGroupItem className="d-flex justify-content-between">
                  <span className="text-muted">Груженый / пустой</span>
                  <strong>
                    {fmtNum(kpis.mileage_loaded_km, 1)} / {fmtNum(kpis.mileage_empty_km, 1)} км
                  </strong>
                </CListGroupItem>
                <CListGroupItem className="d-flex justify-content-between">
                  <span className="text-muted">КИП (использование пробега)</span>
                  <strong>{fmtNum(kpis.KIP)} %</strong>
                </CListGroupItem>
                <CListGroupItem className="d-flex justify-content-between">
                  <span className="text-muted">Плечо откатки (медиана)</span>
                  <strong>{fmtNum(averages.distance_loaded_km, 2)} км</strong>
                </CListGroupItem>
                <CListGroupItem className="d-flex justify-content-between">
                  <span className="text-muted">
                    Скорость груженый / пустой
                    <SafeTooltip content="Средняя скорость в движении: GPS-пробег за время классов «перемещение груженый» и «перемещение пустой». Стоянки и холостой ход не входят, отрезки без точек GPS не учитываются.">
                      <CIcon icon={cilInfo} size="sm" className="text-info ms-1" style={{ cursor: "help" }} />
                    </SafeTooltip>
                  </span>
                  <strong>
                    {fmtNum(kpis.avg_speed_loaded_kmh)} / {fmtNum(kpis.avg_speed_empty_kmh)} км/ч
                  </strong>
                </CListGroupItem>
                <CListGroupItem className="d-flex justify-content-between">
                  <span className="text-muted">Грузооборот</span>
                  <strong>{fmtNum(kpis.cargo_turnover_tkm)} т·км</strong>
                </CListGroupItem>
                <CListGroupItem className="d-flex justify-content-between">
                  <span className="text-muted">Рейсов / объем на моточас</span>
                  <strong>
                    {fmtNum(kpis.trips_per_engine_hour, 2)} / {fmtNum(kpis.volume_per_engine_hour, 2)} м³
                  </strong>
                </CListGroupItem>
                <CListGroupItem className="d-flex justify-content-between bg-light">
                  <span className="text-muted small">
                    Качество данных: GPS без IMU
                    <SafeTooltip content="Пробег по GPS в моменты, когда нет данных IMU. Обычно это дрожание координат на стоянке или необработанные архивы.">
                      <CIcon icon={cilInfo} size="sm" className="text-info ms-1" style={{ cursor: "help" }} />
                    </SafeTooltip>
                  </span>
                  <span className="small fw-bold text-muted">
                    {fmtNum(kpis.mileage_no_imu_km, 1)} км / нет данных {formatDuration(durations.no_data || 0)}
                  </span>
                </CListGroupItem>
              </CListGroup>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* ===================== ТАБЛИЦА РЕЙСОВ ===================== */}
      <CCard className="shadow-sm border-0 mb-4">
        <CCardHeader className="bg-white py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <span className="fw-bold fs-6">Рейсы: {visibleTrips.length}</span>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <CFormCheck
              id="truckOnlyAnomalies"
              label={<span className="small fw-bold text-muted">Только отклонения</span>}
              checked={tripFilters.onlyAnomalies}
              onChange={(e) => setTripFilters((p) => ({ ...p, onlyAnomalies: e.target.checked }))}
              className="mb-0"
            />
            <CFormCheck
              id="truckHideOutOfShift"
              label={<span className="small fw-bold text-muted">Скрыть рейсы вне смены</span>}
              checked={tripFilters.hideOutOfShift}
              onChange={(e) => setTripFilters((p) => ({ ...p, hideOutOfShift: e.target.checked }))}
              className="mb-0"
            />
            <CFormSelect
              size="sm"
              style={{ minWidth: "220px", cursor: "pointer" }}
              className="fw-semibold"
              value={tripSort}
              onChange={(e) => setTripSort(e.target.value)}
            >
              <option value="id_asc">По времени (старые сначала)</option>
              <option value="id_desc">По времени (новые сначала)</option>
              <option value="buckets_desc">По ковшам (больше сначала)</option>
              <option value="buckets_asc">По ковшам (меньше сначала)</option>
              <option value="cycle_desc">По времени рейса (дольше сначала)</option>
              <option value="cycle_asc">По времени рейса (короче сначала)</option>
            </CFormSelect>
          </div>
        </CCardHeader>

        <div className="table-responsive" style={{ maxHeight: "60vh", overflowY: "auto" }}>
          <CTable hover align="middle" small className="mb-0">
            <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <CTableRow>
                <CTableHeaderCell className="ps-3">#</CTableHeaderCell>
                <CTableHeaderCell>Начало рейса</CTableHeaderCell>
                <CTableHeaderCell>Погрузка</CTableHeaderCell>
                <CTableHeaderCell className="text-center">Ковшей</CTableHeaderCell>
                <CTableHeaderCell className="text-center">Время рейса</CTableHeaderCell>
                <CTableHeaderCell style={{ minWidth: "160px" }}>Фазы</CTableHeaderCell>
                <CTableHeaderCell className="text-center">
                  <SafeTooltip content="Ожидание в очереди под погрузкой: от подъезда к экскаватору до первого ковша, вместе с подвижками в очереди. Простой засчитывается очередью по совокупности признаков: рядом с точкой погрузки, чередуется с короткими подъездами, за ним следует погрузка, до него было перемещение пустым. Под временем - сколько из него машина стояла.">
                    <span style={{ borderBottom: "1px dashed #999", cursor: "help" }}>Ожидание погрузки</span>
                  </SafeTooltip>
                </CTableHeaderCell>
                <CTableHeaderCell className="text-center">Груженый / пустой, км</CTableHeaderCell>
                <CTableHeaderCell>
                  <SafeTooltip content="Средняя скорость в движении (перемещение груженым и пустым) и максимальная (95-й процентиль, без выбросов GPS). График - скорость по ходу рейса, цвет как на карте: красный - медленно, зеленый - быстро.">
                    <span style={{ borderBottom: "1px dashed #999", cursor: "help" }}>Скорость, км/ч</span>
                  </SafeTooltip>
                </CTableHeaderCell>
                <CTableHeaderCell>Статус</CTableHeaderCell>
                <CTableHeaderCell className="text-end pe-3">Действия</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {visibleTrips.length === 0 ? (
                <CTableRow>
                  <CTableDataCell colSpan={11} className="text-center text-muted py-4">
                    Рейсов за выбранный период нет
                  </CTableDataCell>
                </CTableRow>
              ) : (
                visibleTrips.map((trip) => {
                  const reasons = anomalyReasons.get(trip.id) || [];
                  const plan = Number(kpis.target_buckets) || 0;
                  let bucketColor = "bg-primary";
                  if (plan > 0 && trip.loading_detected) {
                    const pct = (trip.buckets / plan) * 100;
                    if (pct >= 90 && pct <= 110) bucketColor = "bg-success";
                    else if (pct < 90) bucketColor = "bg-warning";
                    else bucketColor = "bg-danger";
                  }

                  return (
                    <CTableRow key={trip.id} active={trip.id === mapTripId}>
                      <CTableDataCell className="text-muted fw-bold ps-3">{trip.id}</CTableDataCell>
                      <CTableDataCell className="fw-semibold text-dark text-nowrap">
                        {fmtTime(trip.start)}
                        <div className="small text-muted">конец: {fmtTime(trip.end, "HH:mm:ss")}</div>
                      </CTableDataCell>
                      <CTableDataCell className="text-nowrap">
                        {trip.load_start ? (
                          <CButton
                            color="link"
                            size="sm"
                            className="p-0 fw-semibold"
                            title="Смотреть погрузку на видео"
                            onClick={() => jumpTo(trip.load_start, `Погрузка, рейс ${trip.id}`)}
                          >
                            {fmtTime(trip.load_start, "HH:mm:ss")}
                          </CButton>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </CTableDataCell>
                      <CTableDataCell className="text-center">
                        <span className={`badge ${bucketColor} rounded-pill px-3 py-2 shadow-sm`}>{trip.buckets}</span>
                      </CTableDataCell>
                      <CTableDataCell className={`text-center fw-bold ${reasons.length ? "text-danger" : "text-dark"}`}>
                        {formatDuration(trip.cycle_sec || 0)}
                        {reasons.length > 0 && (
                          <SafeTooltip content={reasons.join("; ")}>
                            <CIcon icon={cilWarning} size="sm" className="text-danger ms-1" style={{ cursor: "help" }} />
                          </SafeTooltip>
                        )}
                      </CTableDataCell>
                      <CTableDataCell>
                        <PhaseBar phases={trip.phases} total={trip.cycle_sec} formatDuration={formatDuration} />
                      </CTableDataCell>
                      <CTableDataCell className="text-center text-nowrap">
                        {trip.wait_before_load_sec > 0 ? (
                          <>
                            <span className="fw-semibold">{formatDuration(trip.wait_before_load_sec)}</span>
                            {trip.queue_idle_sec > 0 && (
                              <div className="small text-muted">стоя: {formatDuration(trip.queue_idle_sec)}</div>
                            )}
                          </>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </CTableDataCell>
                      <CTableDataCell className="text-center text-nowrap">
                        {fmtNum(trip.distance_loaded_km, 1)} / {fmtNum(trip.distance_empty_km, 1)}
                      </CTableDataCell>
                      <CTableDataCell className="text-nowrap">
                        <div className="small mb-1">
                          ср. <b>{fmtNum(trip.avg_speed_kmh, 0)}</b> · макс <b>{fmtNum(trip.max_speed_kmh, 0)}</b>
                        </div>
                        <SpeedSparkline series={tripSpeed.series.get(trip.id)} lo={tripSpeed.lo} hi={tripSpeed.hi} />
                      </CTableDataCell>
                      <CTableDataCell>
                        <div className="d-flex flex-wrap gap-1">
                          {tripStatusChips(trip).map((chip, i) => (
                            <CBadge key={i} color={chip.color} shape="rounded-pill">
                              {chip.label}
                            </CBadge>
                          ))}
                        </div>
                      </CTableDataCell>
                      <CTableDataCell className="text-end pe-3 text-nowrap">
                        <CButton
                          size="sm"
                          color="primary"
                          variant="outline"
                          className="fw-bold me-2"
                          title="Трек рейса на карте"
                          onClick={() => showTripOnMap(trip.id)}
                        >
                          <CIcon icon={cilMap} className="me-1" /> Карта
                        </CButton>
                        <CButton
                          size="sm"
                          color="dark"
                          variant="outline"
                          className="fw-bold"
                          onClick={() => jumpTo(trip.start, `Рейс ${trip.id}`)}
                        >
                          <CIcon icon={cilMediaPlay} className="me-1" /> Смотреть
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  );
                })
              )}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      {/* ===================== ДЛИННЫЕ ПРОСТОИ ===================== */}
      <CCard className="shadow-sm border-0 mb-4">
        <CCardHeader className="bg-white fw-bold d-flex justify-content-between align-items-center">
          <span>
            Длинные простои: {idleEvents.length}
            <span className="text-muted fw-normal small ms-2">
              (дольше {Math.round((analyticsData.params?.downtime_threshold_sec || 300) / 60)} мин)
            </span>
          </span>
        </CCardHeader>
        <div className="table-responsive" style={{ maxHeight: "40vh", overflowY: "auto" }}>
          <CTable hover align="middle" small className="mb-0">
            <CTableHead color="light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
              <CTableRow>
                <CTableHeaderCell className="ps-3">Начало</CTableHeaderCell>
                <CTableHeaderCell className="text-center">Длительность</CTableHeaderCell>
                <CTableHeaderCell className="text-center">Двиг. вкл. / выкл.</CTableHeaderCell>
                <CTableHeaderCell className="text-center">Состояние</CTableHeaderCell>
                <CTableHeaderCell className="text-center">Рейс</CTableHeaderCell>
                <CTableHeaderCell className="text-end pe-3">Действия</CTableHeaderCell>
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {idleEvents.length === 0 ? (
                <CTableRow>
                  <CTableDataCell colSpan={6} className="text-center text-muted py-4">
                    Длинных простоев не зафиксировано
                  </CTableDataCell>
                </CTableRow>
              ) : (
                idleEvents.map((ev, idx) => (
                  <CTableRow key={idx}>
                    <CTableDataCell className="fw-semibold text-dark ps-3 text-nowrap">{fmtTime(ev.start)}</CTableDataCell>
                    <CTableDataCell className="text-center fw-bold text-danger">
                      {formatDuration(ev.duration_sec || 0)}
                    </CTableDataCell>
                    <CTableDataCell className="text-center small text-muted text-nowrap">
                      {formatDuration(ev.idle_sec || 0)} / {formatDuration(ev.parked_sec || 0)}
                    </CTableDataCell>
                    <CTableDataCell className="text-center">
                      <CBadge color={ev.state === "loaded" ? "warning" : "secondary"} shape="rounded-pill">
                        {IDLE_STATE_LABELS[ev.state] || ev.state}
                      </CBadge>
                      {!ev.in_schedule && (
                        <>
                          <CBadge color="danger" shape="rounded-pill" className="ms-1">
                            вне смены
                          </CBadge>
                          {/* в журнал простоев (панель управления) он попадает сам, как санкционированный */}
                          <div className="small text-muted mt-1" title="В журнале простоев: «Санкционированный (вне смены)». Можно переклассифицировать вручную">
                            классифицирован автоматически
                          </div>
                        </>
                      )}
                    </CTableDataCell>
                    <CTableDataCell className="text-center text-muted">{ev.trip_id ?? "—"}</CTableDataCell>
                    <CTableDataCell className="text-end pe-3 text-nowrap">
                      <CButton
                        size="sm"
                        color="dark"
                        variant="outline"
                        className="me-2 fw-bold"
                        onClick={() => jumpTo(ev.start, "Простой")}
                      >
                        <CIcon icon={cilMediaPlay} className="me-1" /> Смотреть
                      </CButton>
                      <CButton
                        size="sm"
                        color="danger"
                        variant="outline"
                        className="fw-bold"
                        onClick={() =>
                          setDowntimeModal({
                            visible: true,
                            data: {
                              start_time: new Date(ev.start),
                              end_time: new Date(ev.end),
                              reason: "",
                              comment: `Простой: ${(IDLE_STATE_LABELS[ev.state] || "").toLowerCase()}`,
                            },
                          })
                        }
                      >
                        Классифицировать
                      </CButton>
                    </CTableDataCell>
                  </CTableRow>
                ))
              )}
            </CTableBody>
          </CTable>
        </div>
      </CCard>

      {/* ===================== РАБОТА ВНЕ СМЕНЫ ===================== */}
      {analyticsData.illegal_work
        && (analyticsData.illegal_work.trips > 0 || analyticsData.illegal_work.buckets > 0) && (
          <CRow className="mb-4">
            <CCol xs={12}>
              <CCard className="shadow-sm border-danger border-opacity-50 bg-danger bg-opacity-10">
                <CCardBody className="py-3 d-flex flex-column flex-md-row justify-content-between align-items-center">
                  <div className="d-flex align-items-center mb-2 mb-md-0">
                    <span className="fs-3 me-3">🚨</span>
                    <div>
                      <strong className="text-danger fs-6">Зафиксирована работа вне заданной смены</strong>
                      <div className="text-muted small">Эти операции не входят в основную статистику.</div>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-4 small fw-bold text-dark align-items-center">
                    <div>
                      Рейсов: <span className="text-danger">{analyticsData.illegal_work.trips}</span>
                    </div>
                    <div>
                      Ковшей: <span className="text-danger">{analyticsData.illegal_work.buckets}</span>
                    </div>
                    <div>
                      Объем: <span className="text-danger">{fmtNum(analyticsData.illegal_work.volume)} м³</span>
                    </div>
                    <div>
                      Масса: <span className="text-danger">{fmtNum(analyticsData.illegal_work.mass_t)} т</span>
                    </div>
                    <div>
                      Время работы:{" "}
                      <span className="text-danger">{formatDuration(analyticsData.illegal_work.duration_sec || 0)}</span>
                    </div>
                  </div>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        )}

      {/* ===================== ДЕТАЛИЗАЦИЯ ПО СМЕНАМ ===================== */}
      {analyticsData.has_multiple_shifts && analyticsData.shift_stats && (
        <>
          <div className="text-center mt-4 mb-4 d-flex justify-content-center">
            <CButton
              color="primary"
              className="fw-bold shadow-sm d-flex align-items-center justify-content-center text-white"
              style={{ height: "38px", minWidth: "350px" }}
              onClick={() => setShowDetails(!showDetails)}
            >
              <CIcon icon={showDetails ? cilChevronTop : cilChevronBottom} className="me-2" />
              {showDetails ? "Скрыть детализацию по сменам" : "Развернуть детализацию по сменам"}
            </CButton>
          </div>

          {showDetails && (
            <CRow>
              {Object.entries(analyticsData.shift_stats).map(([name, stats]) => (
                <CCol md={6} key={name} className="mb-4">
                  <CCard className="h-100 shadow-sm border-0">
                    <CCardHeader className="bg-white fw-bold fs-5 text-primary">{name}</CCardHeader>
                    <CListGroup flush>
                      <CListGroupItem className="d-flex justify-content-between border-0 py-1">
                        <span className="text-muted">Рейсы / ковши</span>
                        <strong>
                          {stats.trips} / {stats.buckets}
                        </strong>
                      </CListGroupItem>
                      <CListGroupItem className="d-flex justify-content-between border-0 py-1">
                        <span className="text-muted">Объем, м³</span>
                        <strong>{fmtNum(isSolid ? stats.solid_volume : stats.volume)}</strong>
                      </CListGroupItem>
                      <CListGroupItem className="d-flex justify-content-between border-0 py-1">
                        <span className="text-muted">Масса, т</span>
                        <strong>{fmtNum(stats.mass_t)}</strong>
                      </CListGroupItem>
                      <CListGroupItem className="d-flex justify-content-between border-0 py-1">
                        <span className="text-muted">Груженый / пустой</span>
                        <strong>
                          {formatDuration(durationOf(stats.durations, ACT.HAUL_LOADED))} /{" "}
                          {formatDuration(durationOf(stats.durations, ACT.HAUL_EMPTY))}
                        </strong>
                      </CListGroupItem>
                      <CListGroupItem className="d-flex justify-content-between border-0 py-1">
                        <span className="text-muted">Полезное время</span>
                        <strong>{formatDuration(stats.work_sec || 0)}</strong>
                      </CListGroupItem>
                      <CListGroupItem className="d-flex justify-content-between border-0 py-1">
                        <span className="text-danger fw-bold">Простои</span>
                        <strong className="text-danger">{formatDuration(stats.downtime_sec || 0)}</strong>
                      </CListGroupItem>
                      <CListGroupItem className="d-flex justify-content-between border-0 py-1">
                        <span className="text-muted">Нет данных</span>
                        <strong className="text-muted">{formatDuration(stats.no_data_sec || 0)}</strong>
                      </CListGroupItem>
                    </CListGroup>
                  </CCard>
                </CCol>
              ))}
            </CRow>
          )}
        </>
      )}

      <DowntimeModal
        visible={downtimeModal.visible}
        onClose={() => setDowntimeModal({ visible: false, data: null })}
        deviceId={device?.id}
        initialData={downtimeModal.data}
        onSuccess={() => {}}
      />
    </div>
  );
};

TruckAnalyticsDashboard.propTypes = {
  t: PropTypes.func.isRequired,
  analyticsData: PropTypes.object.isRequired,
  device: PropTypes.object,
  volumeMode: PropTypes.string,
  timezoneOffset: PropTypes.number,
  formatDuration: PropTypes.func.isRequired,
  onTimeJump: PropTypes.func,
  timelineComponent: PropTypes.node,
  mapData: PropTypes.shape({
    track: PropTypes.array,
    loadPoints: PropTypes.array,
    unloadPoints: PropTypes.array,
    caption: PropTypes.string,
  }),
  speedTrack: PropTypes.array,
  mapViewRef: PropTypes.shape({ current: PropTypes.object }),
  onShowAllTrips: PropTypes.func,
  mapTripId: PropTypes.number,
  onMapTripChange: PropTypes.func,
};

// memo: страница перерисовывается при перемотке, открытии окон и т.п. - дашборд с графиками
// при этом не трогаем, пока не изменились его данные
export default React.memo(TruckAnalyticsDashboard);
